import { trackConfig, type IFramework } from '@/framework';
import { LayerType, type ILayerProps, type IVectorLayerProps, type VectorTextInfo } from '@/version-types';
import { pushHistory, requestVectorSVGPreview, resolveVectorTextLayout, updateLayer } from '../utils';

export interface ITextSpacing {
    /**
     * 设置行间距
     *
     * @param layerID - 图层 ID
     * @param value - 行间距（注意：该值为底层数据）
     * @param options - 可选；配置选项；`options.writeHistory` 是否写入历史记录
     * @returns
     */
    setLineSpacing(layerID: string, value: number, options?: { writeHistory: boolean }): ILayerProps | void;

    /**
     * 设置字间距
     *
     * @param layerID - 图层 ID
     * @param value - 字间距（注意：该值为底层数据）
     * @param options - 可选；配置选项；`options.writeHistory` 是否写入历史记录
     */
    setLetterSpacing(layerID: string, value: number, options?: { writeHistory?: boolean }): ILayerProps | void;
}

export class TextSpacing implements ITextSpacing {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    /**
     * 更新行距或字距，并在连续调节阶段复用同一个历史基线。
     *
     * @param layerID - 目标 Vector Layer ID。
     * @param kind - 待更新的间距字段。
     * @param value - 新间距值。
     * @param options - 是否结束连续调节并写入历史。
     * @returns 更新后的 Vector；输入不适用时返回 void。
     */
    private set(
        layerID: string,
        kind: 'lineSpacing' | 'letterSpacing',
        value: number,
        options = { writeHistory: true },
    ) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        const { writeHistory } = options;

        if (writeHistory) {
            const nextLayer = this.framework.service.flatLayer.getLayer(layerID, true);

            // 连续调节结束后只构建一次正式 drawable，并让 Preview 等待该版本接管。
            requestVectorSVGPreview(this.framework, 'commit', nextLayer);
            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);
            this.prevLayer = null;

            return nextLayer;
        }

        const nextTextInfo: VectorTextInfo = { ...textInfo, [kind]: value };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer = resolveVectorTextLayout(this.framework, { ...prevLayer, data: nextLayerData });

        requestVectorSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { dataOnly: true });

        return nextLayer;
    }

    setLetterSpacing(layerID: string, value: number, options = { writeHistory: true }) {
        return this.set(layerID, 'letterSpacing', value, options);
    }

    setLineSpacing(layerID: string, value: number, options = { writeHistory: true }) {
        return this.set(layerID, 'lineSpacing', value, options);
    }
}
